import { computed, type ComputedRef, type Ref, type MaybeRefOrGetter, toValue } from 'vue';
import { Tab } from './tab.enum';
import { BookingStatus } from '@/entities/bookings/booking-status.enum';
import { FindAllBookingsSort } from '@/entities/bookings/find-all-bookings-sort.enum';
import { Order } from '@/entities/pagination/order.enum';
import { DateTime } from 'luxon';
import type {
  FindAllBookingsFilterDto,
  FindAllBookingsQueryDto,
} from '@/entities/bookings/booking.entity';

export const useGetBookingsQuery = (
  activeTab: MaybeRefOrGetter<Tab>,
  searchQuery: MaybeRefOrGetter<string>,
  currentStationId: MaybeRefOrGetter<string | undefined>,
  filterDate: MaybeRefOrGetter<Date>,
  bookingNumberQuery: MaybeRefOrGetter<number | undefined>,
  dateRangeStart?: Ref<Date | null>,
  dateRangeEnd?: Ref<Date | null>,
) => {
  const getGeneralQueryParams = (
    customQueryParams?: FindAllBookingsFilterDto,
  ): FindAllBookingsFilterDto[] => {
    const searchQueryValue = toValue(searchQuery);
    const currentStationIdValue = toValue(currentStationId);
    const fixParams = {
      query: searchQueryValue,
      ...customQueryParams,
    };
    return currentStationIdValue
      ? [
          {
            ...fixParams,
            pickupStationIds: [currentStationIdValue],
          },
          {
            ...fixParams,
            dropoffStationIds: [currentStationIdValue],
          },
          {
            ...fixParams,
            carStationIds: [currentStationIdValue],
          },
        ]
      : [fixParams];
  };

  const getBookingsQuery = computed<FindAllBookingsQueryDto>(() => {
    const activeTabValue = toValue(activeTab);
    const filterDateValue = toValue(filterDate);
    const bookingNumberQueryValue = toValue(bookingNumberQuery);
    switch (activeTabValue) {
      case Tab.NO_CAR:
        return {
          filter: getGeneralQueryParams({
            status: [BookingStatus.FLOATING],
          }),
          sort: FindAllBookingsSort.START_DATE,
          order: Order.ASC,
        };
      case Tab.FOR_REVIEW:
        return {
          filter: getGeneralQueryParams({
            status: [BookingStatus.OPEN],
          }),
          sort: FindAllBookingsSort.START_DATE,
          order: Order.ASC,
        };
      case Tab.ACTIVE:
        return {
          filter: getGeneralQueryParams({
            status: [
              BookingStatus.OPEN,
              BookingStatus.CONFIRMED,
              BookingStatus.HANDED_OVER,
              BookingStatus.CAR_RETURNED,
            ],
            startDateBefore: DateTime.fromJSDate(filterDateValue).endOf('day').toISO() ?? undefined,
            endDateAfter: DateTime.fromJSDate(filterDateValue).startOf('day').toISO() ?? undefined,
          }),
          sort: FindAllBookingsSort.START_DATE,
          order: Order.ASC,
        };
      case Tab.UNPAID_ONLINE:
        return {
          filter: getGeneralQueryParams({
            status: [BookingStatus.DRAFT],
          }),
          sort: FindAllBookingsSort.CREATED_DATE,
          order: Order.ASC,
        };
      case Tab.MANUAL_INVOICING:
        return {
          filter: getGeneralQueryParams({
            hasPeriodicInvoicing: true,
            status: [BookingStatus.CAR_RETURNED],
          }),
          sort: FindAllBookingsSort.START_DATE,
          order: Order.ASC,
        };
      default: {
        // Overlap logic: booking overlaps with filter range if
        // booking starts before filter ends AND booking ends after filter starts
        const filterStart = dateRangeStart?.value;
        const filterEnd = dateRangeEnd?.value;
        const startDateBefore = filterEnd
          ? (DateTime.fromJSDate(filterEnd).endOf('day').toISO() ?? undefined)
          : undefined;
        const endDateAfter = filterStart
          ? (DateTime.fromJSDate(filterStart).startOf('day').toISO() ?? undefined)
          : undefined;
        return {
          filter: getGeneralQueryParams({
            bookingNumbers: bookingNumberQueryValue ? [bookingNumberQueryValue] : undefined,
            status: [
              BookingStatus.FLOATING,
              BookingStatus.OPEN,
              BookingStatus.CONFIRMED,
              BookingStatus.HANDED_OVER,
              BookingStatus.CAR_RETURNED,
              BookingStatus.CANCELED,
              BookingStatus.FINALIZED,
            ],
            startDateBefore,
            endDateAfter,
          }),
          sort: FindAllBookingsSort.START_DATE,
          order: Order.DESC,
        };
      }
    }
  });

  return getBookingsQuery as ComputedRef<FindAllBookingsQueryDto>;
};
